import { Link } from "react-router-dom";
import {
  LayoutGrid,
  Mic2,
  FileText,
  Sparkles,
  CalendarDays,
  CalendarClock,
  MousePointerClick,
  RotateCcw,
  Repeat2,
  BarChart3,
  Settings,
  Zap,
  ArrowRight,
  Check,
  Shield,
  Globe,
} from "lucide-react";
import type { ComponentType, SVGProps, ReactNode } from "react";
import { LandingHeader, LandingFooter } from "../components/landing-chrome";

interface ModuleEntry {
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  badge?: string;
  what: string;
  how: string[];
  notes?: string;
}

const SECTIONS: { label: string; modules: ModuleEntry[] }[] = [
  {
    label: "Setup",
    modules: [
      {
        Icon: Settings,
        title: "Settings",
        badge: "Required",
        what: "Connect social accounts (X / Bluesky / LinkedIn), AI keys, brand assets.",
        how: [
          "Add your OpenAI key — used by every AI feature.",
          "Connect at least one social account: OAuth for X / LinkedIn, App Password for Bluesky.",
          "Optional: upload a brand logo, set posting timezone.",
        ],
        notes:
          "OpenAI key is required for Generate / Hooks mining / Evergreen rewrite. Bluesky needs an App Password from bsky.app/settings/app-passwords (not your login password).",
      },
      {
        Icon: Mic2,
        title: "Voices",
        badge: "Recommended",
        what: "Train a structured voice profile from your past posts. Every AI generation grounds itself in this voice.",
        how: [
          "Click + New voice, paste 3–10 of your past posts (one per line).",
          "Train. Wait a few seconds — quality bar shows weak / fair / good / strong.",
          "Use the voice everywhere: Generate, Hooks Apply, Evergreen rewrite, Auto-plug.",
        ],
        notes:
          "Hypefury offers generic tones; we extract your structural style — sentence rhythm, openers, punctuation habits.",
      },
    ],
  },
  {
    label: "Create",
    modules: [
      {
        Icon: LayoutGrid,
        title: "Projects · Generate",
        what: "Paste one long-form post. Get 5 platform-native variants in one credit.",
        how: [
          "Click + New project, paste 200–3000 chars of long-form.",
          "Pick voice + mood. Hit Generate variants — costs 1 credit total.",
          "Review tabs: X (auto-thread), Bluesky, LinkedIn — these can Schedule / Post now.",
          "Newsletter + Short-video are script-only: Copy / Download .txt to paste into Beehiiv / TikTok / etc.",
        ],
        notes:
          "Hypefury bills per-platform; we charge 1 credit for all 5 variants combined.",
      },
      {
        Icon: FileText,
        title: "Drafts",
        what: "Quick compose with autosave. No AI cost.",
        how: [
          "+ New draft → write title + body + platform.",
          "Autosave kicks in after 1.2s — see Saving... → Saved 5s ago.",
          "Schedule directly from the editor (datetime picker).",
        ],
        notes:
          "Hooks Apply has a Save as draft button that lands here with hook + voice + platform pre-filled.",
      },
      {
        Icon: Sparkles,
        title: "Hooks",
        badge: "AI mining",
        what: "Mine 'opener templates' from YOUR top-performing posts. 7 clusters: contrarian, listicle, story, question, stat, declarative, promise.",
        how: [
          "Click Mine my hooks. AI pulls top 40 posts → extracts hook structures.",
          "Filter by cluster, star favorites, edit templates.",
          "Apply to a new topic: enter topic + platform + voice → get 3 variants in your voice.",
        ],
        notes:
          "Requires published posts with engagement. New accounts will return hooks_added: 0.",
      },
    ],
  },
  {
    label: "Publish",
    modules: [
      {
        Icon: CalendarDays,
        title: "Queue",
        badge: "Auto-fill",
        what: "Define recurring slots once. Drop posts in — they auto-fill the next free slot.",
        how: [
          "Pick an account, click + Slot → choose day-of-week + hour + minute.",
          "Optional: tag with Categories (Tips / Threads / Promo) so different topics land in different slots.",
          "Click + Add to queue with content; it fills the next free slot. Or batch via CSV import.",
        ],
        notes:
          "Slots store your timezone (auto-detected from browser). DST and travel are handled correctly.",
      },
      {
        Icon: CalendarClock,
        title: "Schedule",
        what: "Cross-platform view of every queued post. Filter by status / platform, bulk-delete.",
        how: [
          "Filters: status (pending / published / failed) + 6 platform chips.",
          "Bulk select pending → Delete N selected (already-published rows are protected).",
          "Click published rows to jump to the live post on X / Bluesky / LinkedIn.",
        ],
      },
      {
        Icon: MousePointerClick,
        title: "Auto-plug",
        what: "Every time you publish, BrandEcho schedules a voice-matched follow-up post N hours later.",
        how: [
          "+ New auto-plug → pick platform + voice + delay (default 4h).",
          "Write a plug template (e.g. 'More like this → mylink.com').",
          "Each publish triggers one — never the same words twice, always voice-matched.",
        ],
        notes: "Manual posts (not via BrandEcho) won't trigger this.",
      },
    ],
  },
  {
    label: "Recycle",
    modules: [
      {
        Icon: RotateCcw,
        title: "Evergreen",
        badge: "AI rewrite",
        what: "Post a high-value piece once, recycle it forever — every repost is freshly rewritten in your voice.",
        how: [
          "+ New evergreen post → paste content, pick platform + cadence (14 / 30 days) + max repeats.",
          "Cron triggers AI rewrite at cadence boundaries; the timeline never sees duplicate text.",
          "Expand a post to read every prior rewrite version.",
        ],
        notes:
          "Hypefury reposts the SAME text. We rewrite each time so readers can't tell.",
      },
      {
        Icon: Repeat2,
        title: "Auto-retweet",
        badge: "Zero AI cost",
        what: "Recycle X posts that crossed a like threshold — pure retweet, no AI rewrite.",
        how: [
          "Connect X account first.",
          "+ New config → set min_likes + min/max age + cadence (default 7 days).",
          "Cron picks one match per cadence, newest first if multiple qualify. Run now button for instant testing.",
        ],
        notes:
          "Currently X-only. Requires post_metrics to have data — Analytics cron seeds it every 6h.",
      },
    ],
  },
  {
    label: "Insights",
    modules: [
      {
        Icon: BarChart3,
        title: "Analytics",
        what: "Cross-platform engagement (likes / comments / reposts) refreshed every 6h. Top posts table + optimal-times heatmap.",
        how: [
          "Window: 7 / 30 / 90 / 365 days. Platform: X / Bluesky / LinkedIn.",
          "Top posts sorted by likes — click rows to jump to the original.",
          "Heatmap auto-shifts UTC buckets to your local timezone.",
        ],
        notes:
          "LinkedIn API only returns likes + comments (no reposts). New posts appear after the next 6h cycle.",
      },
    ],
  },
];

export default function FeaturesPage() {
  return (
    <div className="min-h-screen bg-ink-50">
      <LandingHeader />

      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-x-0 -top-32 h-[480px] bg-brand-gradient-soft blur-3xl" />
        <div className="relative mx-auto max-w-5xl px-4 pb-12 pt-16 text-center sm:px-6 sm:pb-20 sm:pt-24">
          <span className="badge-brand">
            <Zap className="h-3 w-3" />
            Full feature tour
          </span>
          <h1 className="mt-5 text-4xl font-bold leading-[1.05] tracking-tight text-ink-900 sm:text-5xl md:text-6xl">
            Everything BrandEcho can do —
            <br />
            <span className="text-gradient">from idea to engagement.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-ink-600 sm:text-lg">
            13 modules across 5 stages. Voice-aware AI generation, native cross-platform
            variants, automation that doesn't repeat itself, and analytics that respect your timezone.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/login" className="btn-brand btn-lg no-underline">
              Try it free
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/pricing" className="btn-secondary btn-lg no-underline">
              See pricing
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 pb-12 sm:px-6">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Platforms" value="5" sub="X · Bluesky · LinkedIn · Newsletter · Short-video" />
          <Stat label="Modules" value="13" sub="Across create / publish / recycle / insights" />
          <Stat label="1 credit" value="= 5 variants" sub="One paste, every channel" />
          <Stat label="Edge" value="Cloudflare" sub="Workers · D1 · R2 · KV" />
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 pb-20 sm:px-6">
        <div className="space-y-14">
          {SECTIONS.map((s) => (
            <SectionBlock key={s.label} label={s.label} modules={s.modules} />
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 pb-24 sm:px-6">
        <div className="overflow-hidden rounded-2xl border border-ink-200/70 bg-white shadow-sm">
          <div className="border-b border-ink-100 bg-brand-gradient-soft px-6 py-4">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-brand-600" />
              <h2 className="font-semibold text-ink-900">
                Golden path · 30 minutes to test the entire app
              </h2>
            </div>
          </div>
          <ol className="divide-y divide-ink-100">
            {GOLDEN_PATH.map((g, i) => (
              <li key={i} className="flex items-start gap-4 px-6 py-3">
                <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-brand-gradient text-2xs font-semibold text-white">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-ink-900">{g.title}</p>
                  <p className="text-xs text-ink-500">{g.detail}</p>
                </div>
                <span className="text-2xs font-medium text-ink-400">{g.eta}</span>
              </li>
            ))}
          </ol>
          <div className="border-t border-ink-100 bg-ink-50 px-6 py-3 text-xs text-ink-500">
            Total ≈ 34 min (excluding cron-driven analytics waits)
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 pb-24 sm:px-6">
        <div className="grid gap-4 md:grid-cols-3">
          <TrustCard
            Icon={Shield}
            title="Your data, your keys"
            body="Bring your own OpenAI / Anthropic key. We never train on your content."
          />
          <TrustCard
            Icon={Globe}
            title="Edge-fast worldwide"
            body="Cloudflare Workers + D1 + R2 — sub-50ms responses anywhere on earth."
          />
          <TrustCard
            Icon={Zap}
            title="Voice-aware everywhere"
            body="Voices ground every generation: Projects, Hooks Apply, Evergreen rewrites, Auto-plug."
          />
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 pb-28 text-center sm:px-6">
        <h2 className="text-2xl font-semibold tracking-tight text-ink-900 sm:text-3xl">
          Ready to write once and publish everywhere?
        </h2>
        <p className="mt-3 text-ink-600">
          7-day free trial. Cancel anytime — no charge if you bail in the trial
          window.
        </p>
        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <Link to="/login" className="btn-brand btn-lg no-underline">
            Start 7-day trial
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/pricing" className="btn-secondary btn-lg no-underline">
            Compare plans
          </Link>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}

const GOLDEN_PATH = [
  { title: "Settings", detail: "Add OpenAI key + connect 1 social account.", eta: "5m" },
  { title: "Voices", detail: "Train one voice with 5 past posts.", eta: "3m" },
  { title: "New project", detail: "Paste long-form → Generate 5 variants.", eta: "2m" },
  { title: "Project · Post now", detail: "Publish one variant to X.", eta: "1m" },
  { title: "Schedule", detail: "Verify it appears as published.", eta: "1m" },
  { title: "Drafts", detail: "Write + autosave + Schedule.", eta: "3m" },
  { title: "Queue", detail: "Add 2 slots + drop a post in.", eta: "3m" },
  { title: "Hooks · Mine", detail: "AI extracts your top hook templates.", eta: "5m" },
  { title: "Hooks · Apply", detail: "Apply a template to a new topic.", eta: "2m" },
  { title: "Auto-plug", detail: "Configure a voice-matched follow-up.", eta: "3m" },
  { title: "Auto-retweet", detail: "Set threshold + Run now to test.", eta: "2m" },
  { title: "Evergreen", detail: "Add a recyclable post + cadence.", eta: "3m" },
  { title: "Analytics", detail: "Switch window + platform.", eta: "1m" },
];

function SectionBlock({
  label,
  modules,
}: {
  label: string;
  modules: ModuleEntry[];
}) {
  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <span className="text-2xs font-semibold uppercase tracking-wider text-brand-600">
          {label}
        </span>
        <div className="h-px flex-1 bg-ink-200/70" />
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {modules.map((m) => (
          <ModuleCard key={m.title} entry={m} />
        ))}
      </div>
    </div>
  );
}

function ModuleCard({ entry }: { entry: ModuleEntry }) {
  const { Icon, title, badge, what, how, notes } = entry;
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-ink-200/70 bg-white p-6 shadow-xs transition-all hover:-translate-y-0.5 hover:shadow-md">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-gradient text-white shadow-sm">
          <Icon className="h-5 w-5" />
        </div>
        {badge ? <span className="badge-brand">{badge}</span> : null}
      </div>
      <h3 className="text-base font-semibold text-ink-900">{title}</h3>
      <p className="mt-1.5 text-sm text-ink-600">{what}</p>
      <ul className="mt-4 space-y-1.5">
        {how.map((step, i) => (
          <li key={i} className="flex gap-2 text-sm text-ink-700">
            <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-500" />
            <span>{step}</span>
          </li>
        ))}
      </ul>
      {notes ? (
        <p className="mt-4 rounded-lg border-l-2 border-brand-300 bg-brand-50/60 px-3 py-2 text-xs text-ink-600">
          {notes}
        </p>
      ) : null}
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="rounded-xl border border-ink-200/70 bg-white p-4 shadow-xs">
      <p className="text-2xs font-medium uppercase tracking-wide text-ink-500">
        {label}
      </p>
      <p className="mt-1 text-xl font-semibold text-ink-900">{value}</p>
      <p className="mt-1 text-xs text-ink-500">{sub}</p>
    </div>
  );
}

function TrustCard({
  Icon,
  title,
  body,
}: {
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  body: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-ink-200/70 bg-white p-6 shadow-xs">
      <Icon className="h-5 w-5 text-brand-600" />
      <h3 className="mt-3 font-semibold text-ink-900">{title}</h3>
      <p className="mt-1.5 text-sm text-ink-600">{body}</p>
    </div>
  );
}
